import { getFoodNamesByIds } from '../data/foods';
import type { ExposureConfigFromColumn } from '@types';

/** Must match `GROUP_SIZE` in scenarioForm/transform.ts (multi-item chunk). */
export const EXPOSURE_MULTI_ITEM_GROUP_SIZE = 4;

export type ResolvedExposureFood = {
  itemIds: number[];
  portataNumber: number | null;
};

export type ExposureFoodDisplayContext = {
  isMultiItem: boolean;
  portate: number[][];
};

function normalizeItemIdsKey(itemIds: number[]): string {
  return [...itemIds].sort((a, b) => a - b).join('|');
}

function toNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(v => (typeof v === 'number' ? v : Number(v)))
    .filter(n => Number.isFinite(n) && n > 0);
}

/** itemIds from exposure config. */
export function getExposureItemIdsFromConfig(
  config?: ExposureConfigFromColumn | null
): number[] | number[][] | undefined {
  if (!config) return undefined;
  if (Array.isArray(config.itemIds)) return config.itemIds;
  return undefined;
}

/** Matrix of gruppi from exposure config (multi-item). */
export function getPortateFromExposureConfig(
  config?: ExposureConfigFromColumn | null
): number[][] {
  const raw = getExposureItemIdsFromConfig(config);
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const first = raw[0];
  if (!Array.isArray(first)) return [];
  return (raw as number[][])
    .map(portata => toNumberArray(portata))
    .filter(portata => portata.length > 0);
}

export type PlannedExposureGroup = {
  label: string;
  itemIds: number[];
};

/** Elementi/gruppi previsti dalla config exposure (stessa logica del form). */
export function getPlannedExposureGroups(
  config?: ExposureConfigFromColumn | null
): PlannedExposureGroup[] {
  if (!config) return [];
  const isMultiItem = Boolean(config.isMultiItem);
  const raw = getExposureItemIdsFromConfig(config);
  if (!Array.isArray(raw) || raw.length === 0) return [];

  if (isMultiItem && Array.isArray(raw[0])) {
    return getPortateFromExposureConfig(config).map((itemIds, index) => ({
      label: `Gruppo ${index + 1}`,
      itemIds,
    }));
  }

  const itemIds = toNumberArray(raw as unknown[]);
  if (itemIds.length === 0) return [];
  return [{ label: 'Elementi', itemIds }];
}

export function findPortataNumber(
  portate: number[][],
  itemIds: number[]
): number | null {
  if (portate.length === 0 || itemIds.length === 0) return null;
  const key = normalizeItemIdsKey(itemIds);
  const index = portate.findIndex(p => normalizeItemIdsKey(p) === key);
  return index >= 0 ? index + 1 : null;
}

function rawPayloadValue(payload: {
  itemIds?: unknown;
  itemId?: unknown;
  foodIds?: unknown;
  foodId?: unknown;
}): unknown {
  if (payload.itemIds !== undefined && payload.itemIds !== null) {
    return payload.itemIds;
  }
  if (typeof payload.itemId === 'number') return payload.itemId;
  if (payload.foodIds !== undefined && payload.foodIds !== null) {
    return payload.foodIds;
  }
  if (typeof payload.foodId === 'number') return payload.foodId;
  return undefined;
}

/**
 * Maps Ably itemShown/itemFeedback payload to stored itemIds.
 * Multi-item: numeric payload is 1-based group index; otherwise single item id.
 */
export function resolveExposureItemFromPayload(
  payload: {
    itemIds?: unknown;
    itemId?: unknown;
    foodIds?: unknown;
    foodId?: unknown;
  },
  config?: ExposureConfigFromColumn | null
): ResolvedExposureFood {
  const raw = rawPayloadValue(payload);
  const isMultiItem = Boolean(config?.isMultiItem);
  const portate = isMultiItem ? getPortateFromExposureConfig(config) : [];

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (isMultiItem) {
      const portataNumber = Math.trunc(raw);
      const itemIds = portate[portataNumber - 1] ?? [];
      return {
        itemIds: [...itemIds],
        portataNumber: itemIds.length > 0 ? portataNumber : null,
      };
    }
    return { itemIds: [raw], portataNumber: null };
  }

  if (Array.isArray(raw)) {
    const itemIds = toNumberArray(raw);
    const portataNumber =
      isMultiItem && itemIds.length > 0
        ? findPortataNumber(portate, itemIds)
        : null;
    return { itemIds, portataNumber };
  }

  return { itemIds: [], portataNumber: null };
}

export function formatExposureItemShown(options: {
  itemIds: number[];
  portataNumber: number | null;
  isMultiItem: boolean;
}): { title: string; detail: string } {
  const names =
    options.itemIds.length > 0
      ? getFoodNamesByIds(options.itemIds)
      : 'Nessun elemento';

  if (options.isMultiItem && options.portataNumber != null) {
    return {
      title: `Gruppo ${options.portataNumber}`,
      detail: `Elementi mostrati: ${names}`,
    };
  }

  return {
    title: 'Nuovo elemento mostrato',
    detail: `Elemento mostrato: ${names}`,
  };
}

export function formatExposureItemFeedback(options: {
  itemIds: number[];
  portataNumber: number | null;
  isMultiItem: boolean;
  score: number;
}): { title: string; detail: string } {
  const names =
    options.itemIds.length > 0
      ? getFoodNamesByIds(options.itemIds)
      : 'Nessun elemento';

  if (options.isMultiItem && options.portataNumber != null) {
    return {
      title: `Feedback gruppo ${options.portataNumber}`,
      detail: `Livello ansia su ${names}: ${options.score}`,
    };
  }

  return {
    title: 'Nuovo Feedback',
    detail: `Livello ansia su ${names}: ${options.score}`,
  };
}

export function getExposureItemEventTypeLabel(
  eventType: 'itemShown' | 'itemFeedback',
  isMultiItem: boolean
): string {
  if (!isMultiItem) {
    return eventType === 'itemShown'
      ? 'Elemento mostrato'
      : 'Feedback elemento';
  }
  return eventType === 'itemShown' ? 'Gruppo mostrato' : 'Feedback gruppo';
}

export function formatExposureItemEventDetail(options: {
  eventType: 'itemShown' | 'itemFeedback';
  itemIds: number[];
  portataNumber: number | null;
  isMultiItem: boolean;
  score?: number;
}): string {
  const names =
    options.itemIds.length > 0 ? getFoodNamesByIds(options.itemIds) : '—';

  if (options.isMultiItem && options.portataNumber != null) {
    const prefix = `Gruppo ${options.portataNumber}`;
    if (options.eventType === 'itemFeedback' && options.score != null) {
      return `${prefix} · ${names} · score: ${options.score}`;
    }
    return `${prefix} · ${names}`;
  }

  if (options.eventType === 'itemFeedback' && options.score != null) {
    const idsPrefix = options.itemIds.length > 0 ? `${names} · ` : '';
    return `${idsPrefix}score: ${options.score}`;
  }

  return names;
}

export function formatExposureItemStatLabel(options: {
  itemIds: number[];
  portataNumber: number | null;
  isMultiItem: boolean;
}): string {
  const names =
    options.itemIds.length > 0 ? getFoodNamesByIds(options.itemIds) : '—';

  if (options.isMultiItem && options.portataNumber != null) {
    return `Gruppo ${options.portataNumber}: ${names}`;
  }

  return names;
}
